/**
 * School Entity Schema
 *
 * Core representation of a school entity used across the platform.
 */

import { z } from 'zod'
import { SchoolStatsDataSchema } from './school-stats'
import { SubscriptionStatusSchema } from './subscription'

/**
 * School Status Enum
 */
export const SchoolStatusSchema = z.enum(['ACTIVE', 'PENDING', 'INACTIVE', 'SUSPENDED'])
export type SchoolStatus = z.infer<typeof SchoolStatusSchema>

/**
 * Data Lookup Connection Schema
 * Connection details for school data lookup integration
 */
export const DataLookupConnectionSchema = z.object({
  id: z.string().nullable().describe('Data connection identifier'),
  platformId: z.string().nullable().describe('Blackbaud platform user ID'),
  schoolUserId: z.string().nullable().describe('Connected school user ID'),
  firstName: z.string().nullable().describe('Connected user first name'),
  lastName: z.string().nullable().describe('Connected user last name'),
  status: z.string().nullable().describe('Connection status')
})

export type DataLookupConnection = z.infer<typeof DataLookupConnectionSchema>

/**
 * Subscription Plan Summary (for school list / detail views)
 *
 * Reflects the new subscription/catalog model (plan #004): a school's primary
 * subscription surfaces its plan + product + lifecycle status. The legacy cost
 * fields (monthlyCost/annualCost/startupCost) were dropped in U3 (KTD-2a) —
 * the platform never charges, so they are intentionally absent here.
 * `id` is the plan's public ID. The richer `Subscription` entity lives in
 * `./subscription`; this is the trimmed shape embedded on the school object.
 */
export const SubscriptionPlanSummarySchema = z.object({
  id: z.string().describe('Plan public ID (cuid)'),
  name: z.string().describe('Plan name'),
  slug: z.string().optional().describe('Plan slug'),
  productName: z.string().optional().describe('Owning product name'),
  // Lifecycle status. Tightened to the strict `SubscriptionStatus` enum now that
  // U6 has repointed the school-API formatter onto the new `Subscription` table
  // (the formatter lowercases the Prisma enum to match).
  status: SubscriptionStatusSchema.optional().describe('Subscription lifecycle status')
})

export type SubscriptionPlanSummary = z.infer<typeof SubscriptionPlanSummarySchema>

/**
 * School Summary Schema
 * Minimal school info for list views and references
 */
export const SchoolSummarySchema = z.object({
  id: z.string().describe('School public ID'),
  name: z.string().describe('School name'),
  slug: z.string().optional().describe('School slug'),
  environmentName: z.string().optional().describe('School environment name')
})

export type SchoolSummary = z.infer<typeof SchoolSummarySchema>

/**
 * School Public Branding Schema
 *
 * Slim, PUBLIC (no-auth) branding surface resolved by slug — name + logos only.
 * Powers unauthenticated school-branded pages (e.g. the per-school privacy
 * policy). Deliberately carries no operational/PII fields. `name` doubles as
 * the school's "company name" for legal copy.
 */
export const SchoolPublicBrandingSchema = z.object({
  name: z.string().describe('School (company) name'),
  slug: z.string().nullable().describe('School slug'),
  logoUrlLight: z.string().nullable().describe('Logo URL for light backgrounds'),
  logoUrlDark: z.string().nullable().describe('Logo URL for dark backgrounds')
})

export type SchoolPublicBranding = z.infer<typeof SchoolPublicBrandingSchema>

/**
 * School List Item Stats Schema
 * Pre-computed stats for school list views (daily + weekly snapshots)
 */
const SchoolListItemStatsPeriodSchema = z.object({
  periodStart: z.coerce.date().describe('Start of the stats period'),
  periodEnd: z.coerce.date().describe('End of the stats period'),
  data: SchoolStatsDataSchema.describe('Stats data for this period')
})

export const SchoolListItemStatsSchema = z.object({
  daily: SchoolListItemStatsPeriodSchema.nullable().describe('Latest daily stats snapshot'),
  weekly: SchoolListItemStatsPeriodSchema.nullable().describe('Latest weekly stats snapshot')
})

export type SchoolListItemStats = z.infer<typeof SchoolListItemStatsSchema>

/**
 * School List Item Schema
 * Full school info for list endpoints
 */
export const SchoolListItemSchema = z.object({
  id: z.string().describe('School unique identifier'),
  name: z.string().nullable().describe('School name'),
  // Needed by the mobile-app creation surfaces to derive bundle ids / package
  // names / config keys from the school without a second per-school fetch.
  slug: z.string().nullable().describe('URL-safe school slug'),
  platform: z.string().nullable().describe('School platform (e.g., Blackbaud)'),
  environmentId: z.string().nullable().describe('Platform environment ID'),
  environmentName: z.string().nullable().describe('Platform environment name'),
  status: SchoolStatusSchema.describe('School status'),
  city: z.string().nullable().describe('School city'),
  state: z.string().nullable().describe('School state/province'),
  country: z.string().nullable().describe('School country'),
  latitude: z.string().nullable().describe('School latitude'),
  longitude: z.string().nullable().describe('School longitude'),
  timezone: z.string().nullable().describe('School timezone'),
  createdAt: z.coerce.date().nullable().describe('Creation timestamp'),
  updatedAt: z.coerce.date().nullable().describe('Last update timestamp'),
  // Stamped when the school's setup sequence completed — i.e. when the school
  // actually went live on the platform. Null for a school row that exists but
  // was never initialized, which is why this is reported separately from
  // `createdAt` rather than folded into it.
  initializedAt: z.coerce.date().nullable().describe('When the school completed setup and went live'),
  totalUsers: z.number().describe('Total number of users at this school'),
  // Users with a real app account (`UsersOnSchools.userId` is set). The
  // remainder of `totalUsers` are platform-only roster rows that have never
  // signed in. Same definition the reporting dashboards use.
  appUsers: z.number().describe('Number of users at this school with an app account'),
  autopilotSyncEnabled: z
    .boolean()
    .describe('Whether the automatic platform-data sync scheduler is engaged for this school'),
  // The kill switch, for the super-admin fleet list (U8 / R11). Derived from
  // the school's OWN `globalFeatures['app-availability']` override row — never
  // the merged system←school config, matching the enforcing resolver, so a
  // platform-default edit cannot make the whole fleet read as offline.
  // DISPLAY ONLY: `resolveSchoolAppAvailability` is the authority.
  appDisabled: z.boolean().describe('Whether this school’s mobile app is currently switched off for all its users'),
  dataLookupConnection: DataLookupConnectionSchema.describe('Data lookup connection details'),
  subscriptionPlan: SubscriptionPlanSummarySchema.nullable().describe('School subscription plan'),
  stats: SchoolListItemStatsSchema.optional().describe('Pre-computed school stats (when include_stats=true)')
})

export type SchoolListItem = z.infer<typeof SchoolListItemSchema>

/**
 * School Slack Channel Pointer
 *
 * The shared Slack channel we run with a school (Slack Connect / guest invites),
 * so a school-scoped automation has somewhere to look a channel up instead of
 * hardcoding one.
 *
 * The ID is the truth: a Slack channel ID is immutable for the life of the
 * channel, while the name is renamed casually and is not one-to-one. The name is
 * a DISPLAY CACHE, allowed to be stale — nothing resolves or posts by it.
 *
 * This is OUR internal ops data, not a school-owned setting. Only a super-admin
 * reads or writes it; the per-school admin who can edit the same school's name
 * and logos cannot.
 */

/**
 * Slack channel IDs as Slack issues them: `C` (a channel, Slack Connect ones
 * included) or `G` (a legacy private group), then uppercase alphanumerics.
 * Bounded at 31 characters, inside the `varchar(32)` column.
 *
 * `D` — a direct message — is deliberately NOT accepted. It is shaped like a
 * channel ID and Slack's API would post to it happily, so nothing downstream
 * would notice; the school's notifications would simply arrive in one person's
 * DMs instead of the shared channel. This is the only place that can tell the
 * difference, so it is the place that refuses.
 */
export const SLACK_CHANNEL_ID_PATTERN = /^[CG][A-Z0-9]{7,30}$/

/**
 * The write-side channel ID. Tolerant on input because an operator pastes rather
 * than types: surrounding whitespace is trimmed and a lowercase paste is
 * upper-cased (Slack IDs are always uppercase, so that normalises rather than
 * reinterprets).
 *
 * Everything else — a `#channel-name`, a channel URL, a workspace ID — is
 * REFUSED, with a message naming the shape and where to find it. A
 * silently-accepted non-ID would be stored as the truth, and every lookup
 * through it would then miss with nothing to show for it.
 */
export const SlackChannelIdSchema = z.string().trim().toUpperCase().regex(SLACK_CHANNEL_ID_PATTERN, {
  message: 'Expected a Slack channel ID like "C09ABCDEF" (Slack ▸ the channel ▸ About ▸ Channel ID), not a name or URL'
})

/**
 * The write-side display name. A leading `#` is stripped because that is how
 * Slack renders a channel everywhere an operator would copy one from, and
 * storing it would make the cached label differ from the API's own `name` for no
 * reason. Bounded at 128, inside the column; Slack's own limit is 80.
 */
export const SlackChannelNameSchema = z
  .string()
  .trim()
  .transform(value => value.replace(/^#+/, '').trim())
  .refine(value => value.length > 0 && value.length <= 128, {
    message: 'Slack channel name must be 1-128 characters once a leading "#" is removed'
  })

/**
 * The read-side pointer pair, as it appears on a school payload. Both nullable:
 * a school with no shared channel is a real state, not a gap.
 */
export const SchoolSlackChannelSchema = z.object({
  slackChannelId: z.string().nullable().describe('Slack channel ID for the school-shared channel (the truth)'),
  slackChannelName: z.string().nullable().describe('Cached Slack channel name for display — may be stale')
})

export type SchoolSlackChannel = z.infer<typeof SchoolSlackChannelSchema>
